import React from 'react';
import { motion } from 'framer-motion';
import { 
  BuildingOfficeIcon, 
  CpuChipIcon, 
  ShieldCheckIcon,
  UserGroupIcon 
} from '@heroicons/react/24/outline';

export default function About() {
  const features = [
    {
      icon: BuildingOfficeIcon,
      title: 'Hospitality Focused',
      description: 'Specialized solutions designed specifically for the unique needs of hotels, resorts, and hospitality businesses.'
    },
    {
      icon: CpuChipIcon,
      title: 'Cutting-Edge Technology',
      description: 'Built with modern, scalable architecture ensuring reliability and performance for your growing business.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Enterprise Security',
      description: 'Bank-level security protocols to protect your data and ensure compliance with industry standards.'
    },
    {
      icon: UserGroupIcon,
      title: 'Dedicated Support',
      description: '24/7 customer support and training to help you maximize the value of our solutions.'
    }
  ];

  return (
    <section id="about" className="py-24 sm:py-32 bg-gradient-to-b from-white to-gray-50">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base font-semibold leading-7 text-primary-600">About Autiin</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Empowering Businesses Through Innovation
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Autiin is a forward-thinking technology company dedicated to transforming how businesses 
              operate in the hospitality and retail sectors. We combine deep industry expertise with 
              cutting-edge technology to deliver solutions that drive growth and efficiency.
            </p>
          </motion.div>
        </div>

        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex flex-col"
              >
                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r from-primary-600 to-secondary-600">
                    <feature.icon className="h-6 w-6 text-white" aria-hidden="true" />
                  </div>
                  {feature.title}
                </dt>
                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                  <p className="flex-auto">{feature.description}</p>
                </dd>
              </motion.div>
            ))}
          </dl>
        </div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-24 sm:mt-32"
        >
          <div className="relative isolate overflow-hidden bg-gradient-to-r from-primary-600 to-secondary-600 px-6 py-24 shadow-2xl rounded-3xl sm:px-24 xl:py-32">
            <div className="absolute -top-32 left-0 w-full h-96 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-32 right-0 w-full h-96 bg-white/10 rounded-full blur-3xl"></div>
            
            <div className="relative mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Our Mission
              </h2>
              <p className="mt-6 text-lg leading-8 text-primary-100">
                To revolutionize business operations through intelligent, user-friendly software solutions 
                that empower organizations to focus on what matters most - delivering exceptional 
                experiences to their customers.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
} 